<template>
  <AttensiCard
    class="policy-access"
    data-cy="policy-access"
    border
    :drop-shadow="false"
  >
    <DynamicHeading :level="3" class="container__header-heading">
      {{ policyAccessTitle }}
    </DynamicHeading>

    <p class="policy-access__description">
      {{ policyAccessSubtitle }}
    </p>

    <section v-if="canEditPolicies" class="policy-access__policies">
      <SearchBar
        :value="searchQuery"
        :search-text="i18n.t('product.POLICY_SEARCH_PLACEHOLDER')"
        class="policy-access__policies-search"
        name="search-policies"
        data-cy="search-input"
        @updateQuery="onUpdateSearchQuery"
      />
      <ul
        v-if="!isPolicyOptionsLoading"
        class="policy-access__policies-list"
        data-cy="policies-custom-access-list"
      >
        <li
          v-for="policy in filteredPolicyOptions"
          :key="policy.id"
          class="policy-access__policies-item"
        >
          <ProductPolicyItem
            :policy="policy"
            :is-base-content-policy="policy.isBaseContentPolicy"
            @onChange="handleProductPolicyChange"
          />
        </li>
      </ul>
      <section v-if="noSearchResults">
        {{ $t('policy.NO_FOUND') }}: <i>"{{ searchQuery }}"</i>
      </section>
    </section>
  </AttensiCard>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import SearchBar from '@/components/generics/SearchBar.vue'
import ProductPolicyItem from '@/components/product/product_components/component_policies/ProductPolicyItem.vue'
import { AttensiCard } from '@attensi/materia'
import DynamicHeading from '@/components/layout/DynamicHeading.vue'
import { useI18n } from '@/hooks/useI18N.js'
import { useGetPolicyOptionsByProductId } from '@/components/product/product_components/component_policies/useGetPolicyOptionsByProductId.ts'

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: '',
  },
  productId: {
    type: String,
    required: false,
    default: undefined,
  },
  isSelfServedProduct: {
    type: Boolean,
    required: false,
    default: false,
  },
})

const emit = defineEmits(['onChange'])
const i18n = useI18n()

const searchQuery = ref('')

const { policyOptions, isLoading: isPolicyOptionsLoading } =
  useGetPolicyOptionsByProductId(props.productId)

const policyAccessTitle = computed(() => {
  if (props.title) {
    return props.title
  }

  return i18n.t('product.POLICY_ACCESS_TITLE')
})

const policyAccessSubtitle = computed(() => {
  if (props.isSelfServedProduct) {
    return i18n.t('product.POLICY_ATTENSI_PRODUCTS_DESCRIPTION')
  }

  return i18n.t('product.POLICY_ACCESS_SUBTITLE')
})

const isSearching = computed(() => {
  return searchQuery.value.length > 0
})

const noSearchResults = computed(() => {
  return filteredPolicyOptions.value.length === 0 && isSearching.value
})

const filteredPolicyOptions = computed(() => {
  return policyOptions.value.filter((policy) => {
    return policy.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  })
})

const canEditPolicies = computed(() => {
  return !props.isSelfServedProduct
})

const handleProductPolicyChange = (newValue) => {
  policyOptions.value.find((policy) => policy.id === newValue.id).value =
    newValue.value

  const selectedPolicies = policyOptions.value.filter(
    (policy) => policy.value !== undefined
  )

  emit('onChange', selectedPolicies)
}

const onUpdateSearchQuery = (searchText) => {
  searchQuery.value = searchText.value
}
</script>

<style lang="scss" scoped>
.policy-access {
  min-height: 350px;
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  overflow: hidden;
  padding-right: 0 !important;

  &__title {
    @include subtitle;
  }

  &__policies-list {
    padding-right: $spacing-s;
    overflow-y: scroll;
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 150px;
  }
  &__policies {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
  }
  &__policies-search {
    width: 100%;
    max-width: 500px;
  }
  &__policies-item {
    border-bottom: 1px solid $color-border;

    &:last-child {
      border-bottom: none;
    }
  }
  &__footer {
    display: flex;
    margin-top: $spacing-3xs;
  }
}
</style>
